import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import {
  TriplePPagination,
  triplePSDatasource,
} from "../../datasource";
import { DumpEvent, DumpEventFilters, DumpEventUpdate, RangeSummary } from "../../models";
import { RootState } from "../../app/store";

export interface DumpEventsState {
  getDumpEvents: APIResult<DumpEvent[]>;
  dumpEvents: DumpEvent[];
  updateDumpEvent: APIResult<void>;
  dumpEventsSummary: RangeSummary | null;
  getDumpEventsSummaryResult: APIResult<RangeSummary>;
}

const initialState: DumpEventsState = {
  getDumpEvents: emptyAPIResult(),
  dumpEvents: [],
  updateDumpEvent: emptyAPIResult(),
  dumpEventsSummary: null,
  getDumpEventsSummaryResult: emptyAPIResult(),
};

export const getDumpEvents = createAsyncThunk(
  "dumpEvents/getDumpEvents",
  async (data: { filters: DumpEventFilters; pagination?: TriplePPagination }) => {
    return triplePSDatasource.getDumpEvents(data.filters, data.pagination);
  }
);

export const getDumpEventsSummary = createAsyncThunk(
  "dumpEvents/getDumpEventsSummary",
  async (data: { filters: DumpEventFilters }) => {
    return triplePSDatasource.getDumpEventSummary(data.filters);
  }
);

export const updateDumpEvent = createAsyncThunk(
  "dumpEvents/updateDumpEvent",
  async (data: { id: string; update: DumpEventUpdate }) => {
    return triplePSDatasource.updateDumpEvent(data.id, data.update);
  }
);

export const dumpEventsSlice = createSlice({
  name: "dumpEvents",
  initialState,
  reducers: {
    resetDumpEvents(state) {
      state.getDumpEvents = initialState.getDumpEvents;
      state.dumpEvents = [];
    },
    resetUpdateDumpEvent(state) {
      state.updateDumpEvent = initialState.updateDumpEvent;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDumpEvents.pending, (state) => {
        state.getDumpEvents = loadingAPIResult();
      })
      .addCase(getDumpEvents.fulfilled, (state, action) => {
        state.getDumpEvents = successAPIResult(action.payload);
        state.dumpEvents = action.payload;
      })
      .addCase(getDumpEvents.rejected, (state, action) => {
        state.getDumpEvents = errorAPIResult(action.error);
      })
      .addCase(getDumpEventsSummary.pending, (state) => {
        state.getDumpEventsSummaryResult = loadingAPIResult();
      })
      .addCase(getDumpEventsSummary.fulfilled, (state, action) => {
        state.getDumpEventsSummaryResult = successAPIResult(action.payload);
        state.dumpEventsSummary = action.payload;
      })
      .addCase(getDumpEventsSummary.rejected, (state, action) => {
        state.getDumpEventsSummaryResult = errorAPIResult(action.error);
      })
      .addCase(updateDumpEvent.pending, (state) => {
        state.updateDumpEvent = loadingAPIResult();
      })
      .addCase(updateDumpEvent.fulfilled, (state) => {
        state.updateDumpEvent = successAPIResult(undefined);
      })
      .addCase(updateDumpEvent.rejected, (state, action) => {
        state.updateDumpEvent = errorAPIResult(action.error);
      });
  },
});

export const { resetDumpEvents, resetUpdateDumpEvent } = dumpEventsSlice.actions;

export const selectDumpEvents = (state: RootState) =>
  state.dumpEvents.dumpEvents;
export const selectGetDumpEventsResult = (state: RootState) =>
  state.dumpEvents.getDumpEvents;
export const selectUpdateDumpEventResult = (state: RootState) =>
  state.dumpEvents.updateDumpEvent;
export const selectDumpEventsSummary = (state: RootState) =>
  state.dumpEvents.dumpEventsSummary;
export const selectGetDumpEventsSummaryResult = (state: RootState) =>
  state.dumpEvents.getDumpEventsSummaryResult;

export default dumpEventsSlice.reducer;
